// Campos obrigatórios.
//
// `required: true` no layout (campo ou coluna de tabela) faz o preenchimento
// exigir um valor antes de salvar. A verificação mora aqui, e não espalhada no
// AddDialogForm, porque cada tipo guarda o valor num lugar diferente
// (`value`, `options[].checked`, `body`, `items`, `source`...) — é exatamente o
// tipo de regra que se perde quando é reescrita em cada tela.
//
// Esta é a MESMA regra que o app operacional precisa aplicar; ver
// docs/05-campos-obrigatorios.md.

// Tipos que não coletam nada: marcar como obrigatório não faria sentido.
const STATIC_TYPES = ["title", "title-black", "text-info", "image", "video", "audio", "file"];

// "Vazio" tolerante ao que cada tipo salva: string em branco, lista sem itens,
// objeto sem chaves (o select-func guarda o usuário inteiro).
function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

// Um campo obrigatório está preenchido? Cada tipo responde no lugar onde guarda
// o valor — a lista abaixo é o espelho da tabela "Persistência" do
// docs/02-esquema-json.md.
function isFieldFilled(field) {
  if (!field) return true;

  switch (field.type) {
    case "checkbox":
      return (field.options || []).some((option) => option.checked === true);

    case "signature":
      return !isEmptyValue(field.source);

    case "table":
      return (field.body || []).length > 0;

    case "product-order":
    case "product-table":
      return (field.items || []).length > 0;

    case "form-link":
      return !isEmptyValue(field.target_data_form_id);

    default:
      return !isEmptyValue(field.value);
  }
}

function canBeRequired(type) {
  return !STATIC_TYPES.includes(type);
}

function fieldName(field, index) {
  const label = (field.component && field.component.label) || "";
  return label || `Campo ${index + 1}`;
}

// Mensagem específica por tipo: "preencha" não ajuda quem parou numa tabela.
function missingMessage(field, index) {
  const name = fieldName(field, index);

  switch (field.type) {
    case "checkbox":
      return `${name}: marque ao menos uma opção.`;
    case "signature":
      return `${name}: falta assinar.`;
    case "table":
      return `${name}: lance ao menos uma linha.`;
    case "product-order":
    case "product-table":
      return `${name}: adicione ao menos um produto.`;
    case "form-link":
      return `${name}: selecione o registro vinculado.`;
    default:
      return `${name}: campo obrigatório.`;
  }
}

/**
 * O que falta preencher num formulário.
 *
 * @param {Array} fields campos do layout já mesclados com os valores
 * @returns {Array<{index: number, type: string, message: string}>} vazio = pode salvar
 */
function validateRequired(fields) {
  return (fields || []).reduce((missing, field, index) => {
    if (field.required !== true || !canBeRequired(field.type)) return missing;
    if (isFieldFilled(field)) return missing;

    missing.push({ index, type: field.type, message: missingMessage(field, index) });
    return missing;
  }, []);
}

/**
 * O que falta numa LINHA de tabela, antes de lançá-la.
 *
 * A linha é o registro de verdade da tabela — validar só no salvamento deixaria
 * o usuário lançar dez linhas incompletas e só descobrir no fim.
 *
 * @param {Object} field campo do tipo `table`
 * @returns {Array<{index: number, message: string}>} vazio = pode lançar a linha
 */
function validateRequiredColumns(field) {
  return (field.columns || []).reduce((missing, column, index) => {
    if (column.required !== true || !canBeRequired(column.type)) return missing;
    if (isFieldFilled(column)) return missing;

    missing.push({ index, message: missingMessage(column, index) });
    return missing;
  }, []);
}

// Texto pronto para o alerta: uma linha por pendência.
function missingSummary(missing) {
  return (missing || []).map((item) => `• ${item.message}`).join("\n");
}

export {
  canBeRequired,
  isFieldFilled,
  missingSummary,
  validateRequired,
  validateRequiredColumns,
};
